import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  ExpenseClaimView, ExpenseLimitView, ExpenseTypeView, PerDiemRateView, TravelRequestView,
} from '../types'

const EXP = '/v1/expenses'
const TRV = '/v1/travel'

export function useExpenseTypes() {
  return useQuery({
    queryKey: ['expenses', 'types'],
    queryFn: async () => (await apiClient.get<ExpenseTypeView[]>(`${EXP}/types`)).data,
  })
}

export function useExpenseLimits() {
  return useQuery({
    queryKey: ['expenses', 'limits'],
    queryFn: async () => (await apiClient.get<ExpenseLimitView[]>(`${EXP}/limits`)).data,
  })
}

export function useExpenseClaims() {
  return useQuery({
    queryKey: ['expenses', 'claims'],
    queryFn: async () => (await apiClient.get<ExpenseClaimView[]>(`${EXP}/claims`)).data,
  })
}

export function useExpenseClaim(id: string | undefined) {
  return useQuery({
    queryKey: ['expenses', 'claims', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<ExpenseClaimView>(`${EXP}/claims/${id}`)).data,
  })
}

export function useExpenseMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['expenses'] })

  return {
    saveType: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<ExpenseTypeView>(`${EXP}/types/${id}`, body)).data
          : (await apiClient.post<ExpenseTypeView>(`${EXP}/types`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires a type. A settled claim names it, so it is never deleted. */
    retireType: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${EXP}/types/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    saveLimit: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ExpenseLimitView>(`${EXP}/limits`, body)).data,
      onSuccess: invalidate,
    }),
    createClaim: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ExpenseClaimView>(`${EXP}/claims`, body)).data,
      onSuccess: invalidate,
    }),
    addLine: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<ExpenseClaimView>(`${EXP}/claims/${id}/lines`, body)).data,
      onSuccess: invalidate,
    }),
    submitClaim: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ExpenseClaimView>(`${EXP}/claims/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    approveClaim: useMutation({
      mutationFn: async ({ id, approvedTotal, comment }: {
        id: string; approvedTotal?: number; comment?: string
      }) => (await apiClient.post<ExpenseClaimView>(
        `${EXP}/claims/${id}/approve`, { approvedTotal, comment })).data,
      onSuccess: invalidate,
    }),
    rejectClaim: useMutation({
      mutationFn: async ({ id, comment }: { id: string; comment?: string }) =>
        (await apiClient.post<ExpenseClaimView>(`${EXP}/claims/${id}/reject`, { comment })).data,
      onSuccess: invalidate,
    }),
    reimburse: useMutation({
      mutationFn: async ({ id, method, reimbursedOn }: {
        id: string; method: string; reimbursedOn: string
      }) => (await apiClient.post<ExpenseClaimView>(
        `${EXP}/claims/${id}/reimburse`, { method, reimbursedOn })).data,
      onSuccess: invalidate,
    }),
  }
}

export function usePerDiemRates() {
  return useQuery({
    queryKey: ['travel', 'per-diem'],
    queryFn: async () => (await apiClient.get<PerDiemRateView[]>(`${TRV}/per-diem`)).data,
  })
}

export function useTravelRequests() {
  return useQuery({
    queryKey: ['travel', 'requests'],
    queryFn: async () => (await apiClient.get<TravelRequestView[]>(`${TRV}/requests`)).data,
  })
}

export function useTravelMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['travel'] })
  }

  return {
    saveRate: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<PerDiemRateView>(`${TRV}/per-diem/${id}`, body)).data
          : (await apiClient.post<PerDiemRateView>(`${TRV}/per-diem`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Closes a rate off from today.
     *
     * <p>Dated out rather than deleted: a trip already settled at this rate has to keep
     * resolving to the figure it was paid at, and a rate with no end date silently applies to
     * next year's travel too.
     */
    closeRate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.delete<PerDiemRateView>(`${TRV}/per-diem/${id}`)).data,
      onSuccess: invalidate,
    }),
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<TravelRequestView>(`${TRV}/requests`, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TravelRequestView>(`${TRV}/requests/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: async ({ id, advancePaid }: { id: string; advancePaid?: number }) =>
        (await apiClient.post<TravelRequestView>(
          `${TRV}/requests/${id}/approve`, { advancePaid })).data,
      onSuccess: invalidate,
    }),
  }
}
